// @ts-nocheck
/**
 * Sub-Customers API Client
 */

import { getHttpClient, normalizeEntity } from '@spcc/core'
import type { SubCustomer, SubCustomerFilter, PaginatedSubCustomers } from '../../domain/types'
import type { CreateSubCustomerRequest, UpdateSubCustomerRequest } from '../../domain/types'
import { transformDtoToDomain, validateSubCustomerDto } from '../../api'
import { API_ENDPOINTS } from '../../domain/constants'

function buildListParams(filter: SubCustomerFilter = {}): URLSearchParams {
  const params = new URLSearchParams()

  if (filter.search?.trim()) {
    params.set('filters[$or][0][name][$containsi]', filter.search.trim())
    params.set('filters[$or][1][code][$containsi]', filter.search.trim())
    params.set('filters[$or][2][phone][$containsi]', filter.search.trim())
  }

  // Exclude soft-deleted
  params.set('filters[deletedAt][$null]', 'true')

  if (filter.customerType) {
    params.set('filters[customerType][$eq]', filter.customerType)
  }

  filter.documentIds?.forEach((id, i) => params.set(`filters[documentId][$in][${i}]`, id))

  // Scope to a specific tenant when provided (prevents cross-tenant leakage in admin portal)
  if (filter.tenantDocumentId) {
    params.set('filters[tenant][documentId][$eq]', filter.tenantDocumentId)
  }

  const page = filter.page ?? 1
  const pageSize = filter.pageSize ?? 50
  params.set('pagination[page]', String(page))
  params.set('pagination[pageSize]', String(pageSize))
  params.set('sort', 'name:asc')

  // Populate tenant so we can show which customer a sub-customer belongs to
  params.set('populate[tenant][fields][0]', 'name')
  params.set('populate[tenant][fields][1]', 'code')
  params.set('populate[tenant][fields][2]', 'documentId')

  return params
}

function normalizeStrapiResponse(raw: unknown): SubCustomer[] {
  const items: unknown[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.data)
      ? (raw as any).data
      : []

  return items
    .map((item) => {
      const entity = normalizeEntity(item)
      if (!entity) return null
      try {
        return transformDtoToDomain(validateSubCustomerDto(entity))
      } catch {
        return null
      }
    })
    .filter((x): x is SubCustomer => x !== null)
}

async function getSubCustomers(filter: SubCustomerFilter = {}): Promise<PaginatedSubCustomers> {
  const client = getHttpClient()
  const params = buildListParams(filter)
  const response = await client.get(`${API_ENDPOINTS.LIST}?${params.toString()}`)
  const raw = response?.data ?? response

  const data = normalizeStrapiResponse(raw)
  const meta = (raw as any)?.meta?.pagination ?? {}

  return {
    data,
    pagination: {
      page: meta.page ?? filter.page ?? 1,
      pageSize: meta.pageSize ?? filter.pageSize ?? 50,
      pageCount: meta.pageCount ?? 1,
      total: meta.total ?? data.length,
    },
  }
}

async function getSubCustomer(id: string): Promise<SubCustomer | null> {
  const client = getHttpClient()
  const response = await client.get(API_ENDPOINTS.DETAIL(id))
  const raw = response?.data ?? response
  const entity = normalizeEntity(raw?.data ?? raw)
  if (!entity) return null
  try {
    return transformDtoToDomain(validateSubCustomerDto(entity))
  } catch {
    return null
  }
}

async function createSubCustomer(data: CreateSubCustomerRequest): Promise<SubCustomer> {
  const client = getHttpClient()
  const response = await client.post(API_ENDPOINTS.LIST, { data })
  const raw = response?.data ?? response
  const entity = normalizeEntity(raw?.data ?? raw)
  return transformDtoToDomain(validateSubCustomerDto(entity))
}

async function updateSubCustomer(id: string, data: UpdateSubCustomerRequest): Promise<SubCustomer> {
  const client = getHttpClient()
  const response = await client.put(API_ENDPOINTS.DETAIL(id), { data })
  const raw = response?.data ?? response
  const entity = normalizeEntity(raw?.data ?? raw)
  return transformDtoToDomain(validateSubCustomerDto(entity))
}

async function deleteSubCustomer(id: string): Promise<void> {
  const client = getHttpClient()
  await client.delete(API_ENDPOINTS.DETAIL(id))
}

export const subCustomersApi = {
  getSubCustomers,
  getSubCustomer,
  createSubCustomer,
  updateSubCustomer,
  deleteSubCustomer,
}
